/**
 * 拖曳互動的純計算。
 *
 * 全部以「相對於 pointerdown 的位移」計算，不依賴 getScreenCTM／
 * getBoundingClientRect，因此在 jsdom 中也能完整測試。
 */

import { percent as toPercent, type ISODateTime, type Percent } from '@bigint-project/core';

import type { BarGeometry } from './geometry.js';
import {
  MS_PER_DAY,
  formatDateTime,
  parseDateTime,
  pxToDays,
  snapDays,
  type TimeAxis,
} from './time-scale.js';
import type { GanttTask, LinkAnchor } from './types.js';

export interface DateRange {
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
}

/** 拖曳中段：整條平移，工期不變。 */
export function movedDates(task: GanttTask, axis: TimeAxis, deltaX: number): DateRange {
  const days = snapDays(axis, pxToDays(axis, deltaX));
  const shift = days * MS_PER_DAY;
  return {
    start: formatDateTime(parseDateTime(task.start) + shift),
    finish: formatDateTime(parseDateTime(task.finish) + shift),
  };
}

/** 拖曳右緣：只改完成日（工期），最短為 0（同一時點）。 */
export function resizedDates(task: GanttTask, axis: TimeAxis, deltaX: number): DateRange {
  const days = snapDays(axis, pxToDays(axis, deltaX));
  const startMs = parseDateTime(task.start);
  const finishMs = Math.max(startMs, parseDateTime(task.finish) + days * MS_PER_DAY);
  return { start: task.start, finish: formatDateTime(finishMs) };
}

/** 拖曳進度條右緣：以條寬換算百分比並取整數。 */
export function progressFromDrag(bar: BarGeometry, deltaX: number): Percent {
  if (bar.width <= 0) return toPercent(0);
  const raw = ((bar.progressWidth + deltaX) / bar.width) * 100;
  return toPercent(Math.round(Math.min(100, Math.max(0, raw))));
}

/** 由縱向位移推導拖曳落點的列索引；超出範圍回傳 null。 */
export function linkTargetRow(
  sourceRowIndex: number,
  deltaY: number,
  rowHeight: number,
  rowCount: number,
): number | null {
  if (rowHeight <= 0) return null;
  const index = sourceRowIndex + Math.round(deltaY / rowHeight);
  if (index < 0 || index >= rowCount || index === sourceRowIndex) return null;
  return index;
}

/** 落點在目標條的左半 → start 端；右半 → finish 端。 */
export function anchorAtX(bar: BarGeometry, chartX: number): LinkAnchor {
  if (bar.kind === 'milestone') return chartX < bar.x ? 'start' : 'finish';
  return chartX < bar.x + bar.width / 2 ? 'start' : 'finish';
}

/** 依端點取得條上的 x 座標（供出線／入線使用）。 */
export const anchorX = (bar: BarGeometry, anchor: LinkAnchor): number =>
  anchor === 'start' ? bar.x : bar.endX;
